import { isDevMode } from '@angular/core';
import {
  getPropertyPath,
  isDefinedNotEmpty,
  IsoDate,
  Path,
  Query,
  QueryBuilder,
  QueryFilter,
} from '@softline/core';
import type { AdvancedFilterModel, IServerSideGetRowsRequest } from 'ag-grid-enterprise';
import {
  AmountColumn,
  Column,
  isValueColumn,
  QuantityColumn,
  ValueColumn,
} from '../types/grid';

type FlatColumn<T extends object> = ValueColumn<T> & { columnName: string };

/** Internal filter shape — avoids QueryFilter<T> deep-instantiation when building filters from string column names. */
interface FilterResult {
  type: string;
  name?: string;
  value?: unknown;
}

export function agRequestToQuery<T extends object>(
  columns: Column<T>[],
  request: IServerSideGetRowsRequest,
  externalFilter: QueryFilter<T> | null,
  visibleColIds?: Set<string>
): Query<T> {
  if (request.groupKeys.length !== request.rowGroupCols.length)
    return toGroupRequest(columns, request, externalFilter);
  else return toFlatRequest(columns, request, externalFilter, visibleColIds);
}

export function toGroupRequest<T extends object>(
  columns: Column<T>[],
  request: IServerSideGetRowsRequest,
  externalFilter: QueryFilter<T> | null
): Query<T> {
  const builder = new QueryBuilder<T>();
  builder.start(request.startRow ?? 0);
  builder.end(request.endRow ?? 100);

  const flatColumns = getFlatColumns(columns);

  if (isDefinedNotEmpty(externalFilter)) builder.filter(externalFilter);

  const groupedColumns: string[] = [];
  if (request.rowGroupCols) {
    request.rowGroupCols.forEach((c, index) => {
      if (index <= request.groupKeys?.length) {
        const col = flatColumns.find((o) => o.colId === c.id);
        if (col) {
          builder.column(col.columnName as Path<T>, {
            usage: 'group',
            // dynamic grid columns: names/functions are runtime strings, not statically-typed paths
            func: col.queryFunction as any,
          });
          addSiblingColumn(builder, col);
          groupedColumns.push(col.colId);
          const value =
            request.groupKeys && request.groupKeys.length > index
              ? request.groupKeys[index]
              : undefined;
          if (value !== undefined && value !== null)
            builder.filter(
              toGroupFilter(col, col.columnName, value) as unknown as QueryFilter<T>
            );
        }
      }
    });
  }

  const valueColumns: string[] = [];
  if (request.valueCols) {
    request.valueCols.forEach((v) => {
      const col = flatColumns.find((o) => o.colId === v.id);
      if (col && v.aggFunc) {
        builder.column(col.columnName as Path<T>, {
          usage: 'aggregate',
          agg: v.aggFunc as any,
          func: col.queryFunction as any,
        });
        addSiblingColumn(builder, col);
        valueColumns.push(col.colId);
      }
    });
  }

  if (request.pivotCols) {
    request.pivotCols.forEach((c) => {
      const col = flatColumns.find((o) => o.colId === c.id);
      if (col) {
        builder.column(col.columnName as Path<T>, {
          usage: 'pivot',
          func: col.queryFunction as any,
        });
      }
    });
  }

  applyFilterModel(builder, flatColumns, request);
  applySortModel(builder, flatColumns, request, groupedColumns, valueColumns);

  return builder.build();
}

function toFlatRequest<T extends object>(
  columns: Column<T>[],
  request: IServerSideGetRowsRequest,
  externalFilter: QueryFilter<T> | null,
  visibleColIds?: Set<string>
): Query<T> {
  const builder = new QueryBuilder<T>();
  builder.start(request.startRow ?? 0);
  builder.end(request.endRow ?? 100);

  const flatColumns = getFlatColumns(columns);
  addColumns(builder, flatColumns, visibleColIds);

  if (isDefinedNotEmpty(externalFilter)) builder.filter(externalFilter);

  let index = 0;
  for (const c of request.rowGroupCols) {
    const col = flatColumns.find((o) => o.colId === c.id);
    const value = request.groupKeys[index] as unknown;
    if (col && value !== undefined && value !== null) {
      const queryFilter = toGroupFilter(col, col.columnName, value);
      if (queryFilter) builder.filter(queryFilter as unknown as QueryFilter<T>);
    }
    index++;
  }

  applyFilterModel(builder, flatColumns, request);
  applySortModel(builder, flatColumns, request);

  return builder.build();
}

function applyFilterModel<T extends object>(
  builder: QueryBuilder<T>,
  flatColumns: FlatColumn<T>[],
  request: IServerSideGetRowsRequest
): void {
  if (!request.filterModel) return;

  Object.keys(request.filterModel).forEach((colId) => {
    const col = flatColumns.find((o) => o.colId === colId);
    const filter = request.filterModel?.[colId];
    if (col && filter) {
      const queryFilter = toQueryFilter(col.columnName, filter);
      if (queryFilter) builder.filter(queryFilter as unknown as QueryFilter<T>);
    }
  });
}

function applySortModel<T extends object>(
  builder: QueryBuilder<T>,
  flatColumns: FlatColumn<T>[],
  request: IServerSideGetRowsRequest,
  groupedColumns?: string[],
  valueColumns?: string[]
): void {
  if (!request.sortModel) return;

  for (const s of request.sortModel) {
    if (
      !isDefinedNotEmpty(request.rowGroupCols) &&
      !flatColumns.some((c) => c.colId === s.colId)
    )
      continue;
    if (
      isDefinedNotEmpty(request.rowGroupCols) &&
      groupedColumns &&
      valueColumns &&
      !groupedColumns.includes(s.colId) &&
      !valueColumns.includes(s.colId)
    )
      continue;
    const col = flatColumns.find((o) => o.colId === s.colId);
    if (col)
      builder.sort(
        sortColumnName(col) as Path<T>,
        s.sort.toUpperCase() as 'ASC' | 'DESC'
      );
  }
}

/** An entity column resolves to the record alias, which the backend cannot sort by; the
 *  field to sort on is the one its `sortBy` accessor names. */
function sortColumnName<T extends object>(col: FlatColumn<T>): string {
  if (col.type !== 'entity') return col.columnName;
  const sortBy = (col as { sortBy?: (value: never) => unknown }).sortBy;
  const sortField = sortBy ? getPropertyPath(sortBy) : null;
  return isDefinedNotEmpty(sortField) ? `${col.columnName}.${sortField}` : col.columnName;
}

function toGroupFilter(
  column: { type: string },
  columnName: string,
  value: unknown
): FilterResult {
  switch (column.type) {
    case 'string':
      return { name: columnName, type: 'stringEquals', value: value as string };
    case 'number':
    case 'amount':
    case 'quantity':
      return { name: columnName, type: 'numberEquals', value: value as number };
    case 'date':
      return { name: columnName, type: 'dateEquals', value: value as IsoDate };
    case 'boolean':
      return { name: columnName, type: value ? 'booleanIsTrue' : 'booleanIsFalse' };
    case 'entity':
    case 'object':
      return {
        name: `${columnName}.id`,
        type: 'numberEquals',
        value: (value as { id: number }).id,
      };
    default:
      throw new Error(
        `[agRequestToQuery] toGroupFilter: Unsupported column type: ${column.type}`
      );
  }
}

const TEXT_FILTER_MAP: Record<string, string> = {
  equals: 'stringEquals',
  notEqual: 'stringNotEquals',
  contains: 'stringContains',
  notContains: 'stringNotContains',
  containsIgnoreCase: 'stringContainsIgnoreCase',
  startsWith: 'stringStartsWith',
  endsWith: 'stringEndsWith',
};

const TEXT_UNARY_MAP: Record<string, string> = {
  blank: 'stringEmpty',
  notBlank: 'stringNotEmpty',
};

const NUMBER_FILTER_MAP: Record<string, string> = {
  equals: 'numberEquals',
  notEqual: 'numberNotEquals',
  greaterThan: 'numberGreaterThan',
  lessThan: 'numberLessThan',
  greaterThanOrEqual: 'numberGreaterThanOrEquals',
  lessThanOrEqual: 'numberLessThanOrEquals',
};

const NUMBER_UNARY_MAP: Record<string, string> = {
  blank: 'numberEmpty',
  notBlank: 'numberNotEmpty',
};

const DATE_FILTER_MAP: Record<string, string> = {
  equals: 'dateEquals',
  notEqual: 'dateNotEquals',
  greaterThan: 'dateAfter',
  lessThan: 'dateBefore',
  greaterThanOrEqual: 'dateAfterOrEquals',
  lessThanOrEqual: 'dateBeforeOrEquals',
};

const DATE_UNARY_MAP: Record<string, string> = {
  blank: 'dateEmpty',
  notBlank: 'dateNotEmpty',
};

function toQueryFilter(
  columnName: string,
  filter: AdvancedFilterModel
): FilterResult | null {
  switch (filter.filterType) {
    case 'text': {
      const unary = TEXT_UNARY_MAP[filter.type];
      if (unary) return { name: columnName, type: unary };

      const mapped = TEXT_FILTER_MAP[filter.type];
      if (mapped && isDefinedNotEmpty(filter.filter))
        return { name: columnName, type: mapped, value: filter.filter };
      break;
    }
    case 'number': {
      const unary = NUMBER_UNARY_MAP[filter.type];
      if (unary) return { name: columnName, type: unary };

      const mapped = NUMBER_FILTER_MAP[filter.type];
      if (mapped && isDefinedNotEmpty(filter.filter))
        return { name: columnName, type: mapped, value: filter.filter };
      break;
    }
    case 'date': {
      const unary = DATE_UNARY_MAP[filter.type];
      if (unary) return { name: columnName, type: unary };

      const mapped = DATE_FILTER_MAP[filter.type];
      if (mapped && isDefinedNotEmpty(filter.filter))
        return { name: columnName, type: mapped, value: filter.filter as IsoDate };
      break;
    }
    case 'boolean':
      if (filter.type === 'true') return { name: columnName, type: 'booleanIsTrue' };
      if (filter.type === 'false') return { name: columnName, type: 'booleanIsFalse' };
      break;
    case 'join':
      break;
  }
  return null;
}

function getFlatColumns<T extends object>(columns: Column<T>[]): FlatColumn<T>[] {
  const result: FlatColumn<T>[] = [];

  function traverse(cols: Column<object>[], parentColumnName: string | null): void {
    for (const col of cols) {
      if (col.type === 'group') {
        traverse(col.children as unknown as Column<object>[], parentColumnName);
      } else if (col.type === 'entity') {
        const leafName = getPropertyPath((col as ValueColumn<object>).getValue) ?? null;
        const entityPath = leafName
          ? parentColumnName
            ? `${parentColumnName}.${leafName}`
            : leafName
          : parentColumnName;
        result.push({
          ...(col as unknown as ValueColumn<T>),
          columnName: entityPath ?? '',
        });
        traverse(col.children as unknown as Column<object>[], entityPath);
      } else if (isValueColumn(col)) {
        const leafName = getPropertyPath((col as ValueColumn<object>).getValue) ?? null;
        if (leafName) {
          const columnName = parentColumnName
            ? `${parentColumnName}.${leafName}`
            : leafName;
          result.push({ ...(col as unknown as ValueColumn<T>), columnName });
        } else if (isDevMode()) {
          console.warn(
            `[agRequestToQuery] Column "${col.colId}" has a getValue that does not resolve to a property path. ` +
              `It will be excluded from server queries. If this column should be queried, use a simple property accessor like (row) => row.field.`
          );
        }
      }
    }
  }

  traverse(columns as unknown as Column<object>[], null);
  return result;
}

function addColumns<T extends object>(
  builder: QueryBuilder<T>,
  columns: FlatColumn<T>[],
  visibleColIds?: Set<string>
) {
  // Several displayed columns may resolve to one backend field (icons + id column);
  // request each field once.
  const requested = new Set<string>();
  for (const col of columns) {
    if (visibleColIds && !visibleColIds.has(col.colId)) continue;
    if (col.type !== 'entity')
      addQueryColumn(builder, requested, col.columnName, col.queryFunction);
    for (const name of col.additionalQueryColumns ?? [])
      addQueryColumn(builder, requested, name);
    addSiblingColumn(builder, col, requested);
  }
}

function addQueryColumn<T extends object>(
  builder: QueryBuilder<T>,
  requested: Set<string>,
  name: string,
  queryFunction?: unknown
): void {
  const key = `${name}|${queryFunction ?? ''}`;
  if (requested.has(key)) return;
  requested.add(key);
  builder.column(name as Path<T>, { usage: 'group', func: queryFunction as any });
}

function addSiblingColumn<T extends object>(
  builder: QueryBuilder<T>,
  col: FlatColumn<T>,
  requested?: Set<string>
): void {
  if (col.type === 'amount' || col.type === 'quantity') {
    const fn =
      col.type === 'amount'
        ? (col as unknown as AmountColumn<object>).getCurrency
        : (col as unknown as QuantityColumn<object>).getUnit;
    const leaf = fn ? getPropertyPath(fn) : undefined;
    if (leaf) {
      const dotIdx = col.columnName.lastIndexOf('.');
      const siblingPath =
        dotIdx >= 0 ? col.columnName.substring(0, dotIdx + 1) + leaf : leaf;
      if (requested) addQueryColumn(builder, requested, siblingPath);
      else builder.column(siblingPath as Path<T>);
    }
  }
}
